import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { BehaviorSubject, map, startWith, Subscription } from 'rxjs';
import { IOption } from '../../controls/models/option';
import { AuthService } from '../../../shared/services/auth.service';
import { CommitteeService } from '../shared/services/committee.service';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import { ICommittee } from '../shared/models/committee';
import { ICommitteeSearchData, Option } from '../../ballots/shared/models/sr/sr-committee-search-data';

@Component({
    selector: 'app-select-committee',
    templateUrl: './select-committee.component.html',
    styleUrl: './select-committee.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectCommitteeComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SelectCommitteeComponent implements ControlValueAccessor, OnDestroy {

  @Input('value') _value: ICommittee | undefined;
  @Input() id: string = 'select-committee';
  @Input() label: string = 'Committee';
  @Input() voteOptions: string[] = [];

  @Output() onValueChange? = new EventEmitter();
  @Output() onSelectCommittee?: EventEmitter<string> = new EventEmitter<string>();


  onChange: any = () => { };
  onTouched: any = () => { };

  get value() {
    return this._value;
  }

  set value(val) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  loading: boolean = true;
  subscriptions: Subscription = new Subscription();
  options: IOption<string>[] = [];

  searchForCommittee: ICommitteeSearchData[] = [];
  activeCommittees: string[] = [];
  committee!: ICommittee | null;


  filteredCommitteeOptions: BehaviorSubject<Option[]> = new BehaviorSubject<
    Option[]
  >([]);
  isSelectedCommitteeOption: boolean = false;

  committeeForm = new FormGroup({
    committeeCtrl: new FormControl('', Validators.required)
  });

  constructor(
    private _committeeService: CommitteeService,
  ) {
  }

  ngOnInit() {
      this.initializeSRSCreation();
      this.committeeForm
        .get('committeeCtrl')!
        .valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value || '', this.options))
        )
        .subscribe((filteredOptions) => {
          this.filteredCommitteeOptions.next(filteredOptions);
        });
  }

  private _filter(value: string, options: Option[]): Option[] {
    const filterValue = value.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  initializeSRSCreation() {
    this.getActiveCommittees();
  }

  committeeSelected(committeeCode: string, fromDesignation: boolean = false) {
    this.isSelectedCommitteeOption = true;
    this.subscriptions.add(
      this._committeeService
        .getCommittee(committeeCode)
        .subscribe((response) => {
          this.committee = response.content;
          this.writeValue(this.committee?.committeeCode);
          this.onSelectCommittee?.emit(this.committee?.committeeCode);
        })
    );
  }

  loadingCommitteeOptions() {
    this.filteredCommitteeOptions.next(this.options);
  }

  getActiveCommittees() {
      this.subscriptions.add(
        this._committeeService.getActiveCommittee().subscribe((response) => {
          this.searchForCommittee = response.content;
          if (response && this.searchForCommittee.length > 0) {
            this.options = this.searchForCommittee.map((committee) => {
              return {
                label: `${committee.committeeCode} - ${committee.committeeName}`,
                value: committee.committeeCode,
              } as Option;
            });
          }
        })
      );
  }

  handleCommitteeInputChange(event: Event) {
    this.getActiveCommittees();
    this.isSelectedCommitteeOption = false;
    this.resetCommitteeDetails();
    this.onValueChange?.emit();
  }

  resetCommitteeDetails(){
    this.writeValue('');
    this.onSelectCommittee?.emit('');
    this.committee = null;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
